<script setup>
import { computed, nextTick, ref } from 'vue'
import RelatedCard from './RelatedCard.vue'

/**
 * A list of what an element of the catalogue is related to, edited where it is shown: its name and
 * ⓘ with what being on it means, "Add" in the heading — a search among the options — and each row
 * leading to the element, taken out with its ✕. Read only, it is the same card without them.
 *
 * items:   [{ key, label, to?, hint?, code? }] — the rows as they stand;
 * options: [{ value, label, hint?, group? }] — what may be added (those already on it left out by `taken`);
 * `add(value, target)`: target null — a new row; a row's key — added to that row (`addTo` in the row slot).
 * sortable: the rows' order matters (D-330: a component's texts as offers print them) — dragged by
 *           the handle or moved with the arrow keys; `move(from, to)` told, the host keeps the order.
 */
const props = defineProps({
  title: { type: String, required: true },
  icon: { type: String, default: '' },
  tip: { type: String, default: '' },
  hint: { type: String, default: '' },
  items: { type: Array, default: () => [] },
  options: { type: Array, default: () => [] },
  taken: { type: Array, default: () => [] },
  editable: Boolean,
  placeholder: { type: String, default: '' },
  limit: { type: Number, default: 15 },
  // What is typed may be made new here (D-323: a draft), named so — e.g. "a component".
  creatable: { type: String, default: '' },
  sortable: Boolean,
})
const emit = defineEmits(['add', 'remove', 'create', 'move'])
const dragged = ref(null)
const over = ref(null)
const canSort = computed(() => props.editable && props.sortable && props.items.length > 1)
function moveTo(from, to) {
  if (to < 0 || to >= props.items.length || from === to) return
  emit('move', from, to)
}
function dropAt(i) {
  if (dragged.value !== null) moveTo(dragged.value, i)
  dragged.value = null
  over.value = null
}
function sortKey(e, i) {
  if (e.key === 'ArrowUp') moveTo(i, i - 1)
  else if (e.key === 'ArrowDown') moveTo(i, i + 1)
  else return
  e.preventDefault()
}

const target = ref(undefined)
const query = ref('')
const input = ref(null)
const fold = (v) => String(v || '').toLowerCase().replace(/ł/g, 'l').normalize('NFD').replace(/[̀-ͯ]/g, '')
const found = computed(() => {
  const words = fold(query.value).split(/\s+/).filter(Boolean)
  const free = props.options.filter((o) => !props.taken.includes(o.value))
  return (words.length ? free.filter((o) => words.every((w) => fold(`${o.group || ''} ${o.label} ${o.hint || ''}`).includes(w))) : free).slice(0, props.limit)
})
async function open(key = null) {
  target.value = key
  query.value = ''
  await nextTick()
  input.value?.focus()
}
const close = () => (target.value = undefined)
// Several may be added one after another: the search stays open; to a row, one at a time.
function pick(o) {
  emit('add', o.value, target.value)
  if (target.value !== null) close()
}
// Made new from what is typed: none found by that very name.
const canCreate = computed(() => !!props.creatable && target.value === null && query.value.trim().length > 1
  && !props.options.some((o) => fold(o.label) === fold(query.value.trim())))
function create() {
  emit('create', query.value.trim())
  close()
}
defineExpose({ open })
</script>

<template>
  <RelatedCard :title="title" :icon="icon" :tip="tip" :hint="hint" :count="items.length" :empty="false" :list-tip="false">
    <template #actions>
      <slot name="actions" />
      <button v-if="editable && target === undefined" type="button" class="link text-xs" data-testid="relation-add" @click="open()"><i class="fa-solid fa-plus" aria-hidden="true"></i> {{ $t('common.add') }}</button>
    </template>
    <!-- the search: what may be added, a click adds it -->
    <div v-if="target !== undefined" class="border-b border-default bg-[var(--surface-2)] px-4 py-2" data-testid="relation-search">
      <div class="flex items-center gap-2">
        <i class="fa-solid fa-magnifying-glass text-xs text-faint" aria-hidden="true"></i>
        <input ref="input" v-model="query" class="input flex-1 py-1 text-sm" :placeholder="placeholder" @keydown.esc="close" @keydown.enter.prevent="found[0] && pick(found[0])" />
        <button type="button" class="p-1 text-faint hover:text-[var(--text)]" :title="$t('common.close')" :aria-label="$t('common.close')" @click="close"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>
      </div>
      <button v-if="canCreate" type="button" class="mt-1 flex w-full items-center gap-2 rounded px-2 py-1 text-left text-brand-700 hover:bg-[var(--surface-3)] dark:text-brand-300" data-testid="relation-create" @click="create">
        <i class="fa-solid fa-file-circle-plus text-xs" aria-hidden="true"></i><span>{{ $t('catalogue.relation.create', { what: creatable, name: query.trim() }) }}</span>
      </button>
      <ul v-if="found.length" class="mt-1 max-h-64 overflow-y-auto">
        <li v-for="o in found" :key="o.value">
          <button type="button" class="flex w-full items-start gap-2 rounded px-2 py-1 text-left hover:bg-[var(--surface-3)]" @click="pick(o)">
            <i class="fa-solid fa-plus mt-1 text-xs text-faint" aria-hidden="true"></i>
            <span class="min-w-0 flex-1"><span class="break-words">{{ o.group ? `${o.group} › ${o.label}` : o.label }}</span><span v-if="o.hint" class="block truncate text-xs text-muted" :title="o.hint">{{ o.hint }}</span></span>
          </button>
        </li>
      </ul>
      <p v-else-if="!canCreate" class="mt-1 text-xs text-faint">{{ $t('common.nothingFound') }}</p>
    </div>
    <ul class="divide-y divide-[var(--border)]">
      <li v-for="(item, i) in items" :key="item.key" :class="['flex items-start gap-2 px-4 py-1.5', over === i && dragged !== i ? 'bg-brand-50 dark:bg-brand-900/30' : '']" data-testid="relation-row"
        @dragover.prevent="canSort && (over = i)" @drop.prevent="canSort && dropAt(i)">
        <button v-if="canSort" type="button" draggable="true" class="mt-0.5 cursor-grab text-faint focus:outline-none focus-visible:text-brand-600" :title="$t('catalogue.order.drag')" :aria-label="`${$t('catalogue.order.drag')}: ${item.label}`"
          data-testid="relation-handle" @dragstart="dragged = i" @dragend="dragged = null; over = null" @keydown="sortKey($event, i)"><i class="fa-solid fa-grip-vertical text-xs" aria-hidden="true"></i></button>
        <div class="min-w-0 flex-1">
          <slot name="row" :item="item" :index="i" :add-to="open">
            <router-link v-if="item.to" :to="item.to" class="link break-words">{{ item.label }}</router-link><span v-else class="break-words">{{ item.label }}</span>
            <span v-if="item.code" class="ml-1.5 text-xs text-faint">{{ item.code }}</span>
            <span v-if="item.hint" class="block text-xs text-muted">{{ item.hint }}</span>
          </slot>
        </div>
        <slot name="row-actions" :item="item" :index="i" />
        <button v-if="editable" type="button" class="p-1 text-faint hover:text-red-600" :title="$t('common.remove')" :aria-label="$t('common.remove')" data-testid="relation-remove" @click="emit('remove', item.key)"><i class="fa-solid fa-xmark text-xs" aria-hidden="true"></i></button>
      </li>
      <li v-if="!items.length" class="px-4 py-2 text-faint">—</li>
    </ul>
  </RelatedCard>
</template>
